(() => {
  const STORAGE_KEY = 'owPlantZonePreferences';
  const IP_ZIP_STORAGE_KEY = 'owSuggestedZip';
  const LOOKUP_URL = 'https://services1.arcgis.com/vdxp8SwMGji0hqly/ArcGIS/rest/services/Plant_Hardiness_Zones_/FeatureServer/1/query';
  const IP_LOOKUP_URL = 'https://ipwho.is/';
  let localZoneMapPromise;

  const I18N_DEFAULTS = {
    greatLabel: 'Great fit for your zone',
    greatProductLabel: 'Great match',
    protectLabel: 'Grow in a pot / protect from freezes',
    protectProductLabel: 'Freeze protection recommended',
    notLabel: 'Not recommended outdoors year-round',
    typicalHardiness: 'Typical USDA hardiness: Zones {zones}.',
    moveOrCover: 'Move or cover it during freezes.',
    winterTooCold: 'Your winter lows may be too cold.',
    protectYoung: 'Protect young plants and tender growth during freezes.',
    confirmVariety: 'Confirm the variety and provide the right summer conditions.',
    moreSun: 'Fruiting is usually best with more direct sun.',
    suggestedZip: 'Suggested ZIP {zip} from your approximate location. Confirm or change it.',
    photoSoon: 'Photo coming soon',
    viewPlant: 'View plant',
    invalidZip: 'Enter a valid 5-digit ZIP Code.',
    zipNotFound: 'We could not find that ZIP Code in the USDA map.',
    lookupUnavailable: 'The USDA lookup is temporarily unavailable. Please try again.',
    currentMatches: '{count} current matches',
    viewAllMatches: 'View all {count} matches',
    typicalHardinessShort: 'Typical hardiness: USDA Zones {zones}.',
    checking: 'Checking...',
    check: 'Check',
    typicalWinterLow: 'Typical winter low {temperature}°F',
    strongOutdoorMatch: 'This plant is a strong outdoor match for your winter lows.'
  };

  function readI18n() {
    try {
      return { ...I18N_DEFAULTS, ...JSON.parse(document.querySelector('[data-zone-i18n]')?.textContent || '{}') };
    } catch (_) {
      return I18N_DEFAULTS;
    }
  }

  const i18n = readI18n();
  const format = (template, values = {}) => Object.entries(values).reduce(
    (output, [key, value]) => output.replaceAll(`{${key}}`, value),
    String(template || '')
  );

  const PROFILES = [
    { match: /pineapple guava|feijoa/, min: 8, max: 11, label: '8–11' },
    { match: /barbados cherry|acerola/, min: 9.5, max: 11, label: '9b–11', freezeSensitive: true },
    { match: /sugar apple|atemoya|thai lessard/, min: 10, max: 11, label: '10–11', freezeSensitive: true },
    { match: /star apple|caimito/, min: 10.5, max: 12, label: '10b–12', freezeSensitive: true },
    { match: /soursop|graviola/, min: 10.5, max: 12, label: '10b–12', freezeSensitive: true },
    { match: /passion fruit/, min: 9, max: 11, label: '9–11', freezeSensitive: true, fullSun: true },
    { match: /dragon fruit/, min: 10, max: 11, label: '10–11', freezeSensitive: true },
    { match: /papaya/, min: 10, max: 12, label: '10–12', freezeSensitive: true, fullSun: true },
    { match: /banana|musa/, min: 9, max: 11, label: '9–11', freezeSensitive: true, fullSun: true },
    { match: /coffee/, min: 10, max: 11, label: '10–11', freezeSensitive: true },
    { match: /pink guava|tropical guava|guava tree/, min: 9.5, max: 11, label: '9b–11', freezeSensitive: true },
    { match: /fig/, min: 7, max: 10, label: '7–10', fullSun: true },
    { match: /mulberry/, min: 5, max: 10, label: '5–10', fullSun: true },
    { match: /blackberry|raspberry/, min: 5, max: 10, label: '5–10', fullSun: true },
    { match: /pomegranate/, min: 7, max: 10, label: '7–10', fullSun: true },
    { match: /olive/, min: 8, max: 11, label: '8–11', fullSun: true },
    { match: /blueberry/, min: 7, max: 10, label: '7–10', fullSun: true },
    { match: /grape/, min: 6, max: 10, label: '6–10', fullSun: true },
    { match: /kiwi/, min: 7, max: 10, label: '7–10', fullSun: true },
    { match: /goji/, min: 5, max: 9.5, label: '5–9b', fullSun: true },
    { match: /cherry/, min: 7, max: 10, label: '7–10', fullSun: true },
    { match: /lemon|lime|orange|mandarin|tangerine|grapefruit|kumquat|calamondin|citrus/, min: 9, max: 11, label: '9–11', freezeSensitive: true, fullSun: true },
    { match: /apple/, min: 4, max: 9, label: '4–9', fullSun: true },
    { match: /peach|nectarine/, min: 5, max: 9, label: '5–9', fullSun: true },
    { match: /pear/, min: 4, max: 9, label: '4–9', fullSun: true },
    { match: /plum|apricot/, min: 5, max: 9, label: '5–9', fullSun: true },
    { match: /cold hardy avocado/, min: 8, max: 11, label: '8–11', freezeSensitive: true, fullSun: true },
    { match: /avocado/, min: 9, max: 11, label: '9–11', freezeSensitive: true, fullSun: true },
    { match: /lychee|mango|sapodilla|jackfruit/, min: 10, max: 11, label: '10–11', freezeSensitive: true, fullSun: true },
    { match: /loquat/, min: 8, max: 10, label: '8–10', fullSun: true },
    { match: /persimmon/, min: 7, max: 10, label: '7–10', fullSun: true },
    { match: /elderberry/, min: 4, max: 9, label: '4–9', fullSun: true },
    { match: /pawpaw/, min: 5, max: 9, label: '5–9' },
    { match: /jujube/, min: 6, max: 10, label: '6–10', fullSun: true },
    { match: /pineapple/, min: 10, max: 11, label: '10–11', freezeSensitive: true, fullSun: true }
  ];

  const statusDetails = {
    great: { label: i18n.greatLabel, productLabel: i18n.greatProductLabel, rank: 0 },
    protect: { label: i18n.protectLabel, productLabel: i18n.protectProductLabel, rank: 1 },
    not: { label: i18n.notLabel, productLabel: i18n.notLabel, rank: 2 }
  };

  function zoneLabel(value) {
    const number = Number(value);
    if (!Number.isFinite(number)) return '';
    return Number.isInteger(number) ? String(number) : `${Math.floor(number)}b`;
  }

  function getProfile(title, handle = '', metadata = {}) {
    const searchValue = `${title || ''} ${handle || ''}`.toLowerCase();
    const fallback = PROFILES.find((profile) => profile.match.test(searchValue)) || {};
    const minimum = Number(metadata.zoneMin);
    const maximum = Number(metadata.zoneMax);
    if (Number.isFinite(minimum) && Number.isFinite(maximum) && minimum >= 1 && maximum <= 13.5 && minimum <= maximum && Number.isInteger(minimum * 2) && Number.isInteger(maximum * 2)) {
      return {
        ...fallback,
        min: minimum,
        max: maximum,
        label: `${zoneLabel(minimum)}–${zoneLabel(maximum)}`
      };
    }
    return fallback.min !== undefined ? fallback : null;
  }

  function zoneNumber(zone) {
    const match = String(zone || '').trim().toLowerCase().match(/^(\d{1,2})([ab])?$/);
    if (!match) return null;
    return Number(match[1]) + (match[2] === 'b' ? 0.5 : 0);
  }

  function recommendation(profile, zone, preferences) {
    const numericZone = zoneNumber(zone);
    if (!profile || numericZone === null) return null;

    let status = 'great';
    let note = format(i18n.typicalHardiness, { zones: profile.label });
    const canProtect = preferences.protect === 'yes' || preferences.planting === 'container';

    if (numericZone < profile.min) {
      status = canProtect ? 'protect' : 'not';
      note = canProtect
        ? `${format(i18n.typicalHardiness, { zones: profile.label })} ${i18n.moveOrCover}`
        : `${format(i18n.typicalHardiness, { zones: profile.label })} ${i18n.winterTooCold}`;
    } else if (profile.freezeSensitive && numericZone <= profile.min + 0.5) {
      status = 'protect';
      note = `${format(i18n.typicalHardiness, { zones: profile.label })} ${i18n.protectYoung}`;
    } else if (numericZone > profile.max) {
      status = 'protect';
      note = `${format(i18n.typicalHardiness, { zones: profile.label })} ${i18n.confirmVariety}`;
    }

    if (profile.fullSun && preferences.sun === 'partial') {
      note += ` ${i18n.moreSun}`;
    }

    return { status, note, ...statusDetails[status] };
  }

  function readPreferences() {
    try {
      return JSON.parse(localStorage.getItem(STORAGE_KEY) || 'null');
    } catch (_) {
      return null;
    }
  }

  function savePreferences(preferences) {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(preferences));
    } catch (_) {
      // The finder still works when storage is blocked.
    }
  }

  function readSuggestedZip() {
    try {
      const zip = sessionStorage.getItem(IP_ZIP_STORAGE_KEY) || '';
      return /^\d{5}$/.test(zip) ? zip : '';
    } catch (_) {
      return '';
    }
  }

  function saveSuggestedZip(zip) {
    try {
      sessionStorage.setItem(IP_ZIP_STORAGE_KEY, zip);
    } catch (_) {
      // A suggestion is optional when session storage is blocked.
    }
  }

  function applyZipSuggestion(input, hint, zip) {
    if (!input || input.value || !/^\d{5}$/.test(zip)) return;
    input.value = zip;
    input.dataset.suggestedZip = zip;
    if (hint) {
      hint.textContent = format(i18n.suggestedZip, { zip });
    }
  }

  async function suggestZipFromIp(input, hint) {
    if (!input || input.value || readPreferences()?.zip) return;
    const cached = readSuggestedZip();
    if (cached) {
      applyZipSuggestion(input, hint, cached);
      return;
    }

    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 2200);
    try {
      const response = await fetch(IP_LOOKUP_URL, { signal: controller.signal });
      if (!response.ok) return;
      const data = await response.json();
      const zip = String(data?.postal || '').match(/\d{5}/)?.[0] || '';
      if (data?.success !== false && data?.country_code === 'US' && zip) {
        saveSuggestedZip(zip);
        applyZipSuggestion(input, hint, zip);
      }
    } catch (_) {
      // Manual ZIP entry remains available when approximate lookup is unavailable.
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function bindZipSuggestion(input, hint) {
    if (!input) return;
    input.addEventListener('input', () => {
      input.value = input.value.replace(/\D/g, '').slice(0, 5);
      if (input.dataset.suggestedZip && input.value !== input.dataset.suggestedZip) {
        delete input.dataset.suggestedZip;
        if (hint) hint.textContent = '';
      }
    });
  }

  async function loadLocalZoneMap(url) {
    if (!url) return null;
    if (!localZoneMapPromise) {
      localZoneMapPromise = fetch(url, { cache: 'force-cache', credentials: 'same-origin' })
        .then((response) => {
          if (!response.ok) throw new Error('local_lookup_failed');
          return response.json();
        })
        .then((data) => data?.zones || null)
        .catch(() => null);
    }
    return localZoneMapPromise;
  }

  async function lookupZone(zip, localMapUrl = '') {
    const localMap = await loadLocalZoneMap(localMapUrl);
    const localMatch = localMap?.[zip];
    if (Array.isArray(localMatch) && localMatch[0]) {
      return { zipcode: zip, zone: localMatch[0], trange: localMatch[1] || '' };
    }

    const params = new URLSearchParams({
      where: `zipcode=${zip}`,
      outFields: 'zipcode,zone,trange,zonetitle',
      returnGeometry: 'false',
      f: 'json'
    });
    const controller = new AbortController();
    const timeout = window.setTimeout(() => controller.abort(), 7000);

    try {
      const response = await fetch(`${LOOKUP_URL}?${params}`, { signal: controller.signal });
      if (!response.ok) throw new Error('lookup_failed');
      const data = await response.json();
      const attributes = data?.features?.[0]?.attributes;
      if (!attributes?.zone) throw new Error('zip_not_found');
      return attributes;
    } finally {
      window.clearTimeout(timeout);
    }
  }

  function createProductCard(product, result) {
    const card = document.createElement('article');
    card.className = 'ow-zone-card';

    const media = document.createElement('a');
    media.className = 'ow-zone-card__media';
    media.href = product.url;
    if (product.image) {
      const image = document.createElement('img');
      image.src = product.image;
      image.alt = product.imageAlt || product.title;
      image.loading = 'lazy';
      image.width = 560;
      image.height = 560;
      media.append(image);
    } else {
      const placeholder = document.createElement('span');
      placeholder.className = 'ow-zone-card__placeholder';
      placeholder.textContent = i18n.photoSoon;
      media.append(placeholder);
    }

    const body = document.createElement('div');
    body.className = 'ow-zone-card__body';

    const status = document.createElement('span');
    status.className = `ow-zone-status ow-zone-status--${result.status}`;
    status.textContent = result.label;

    const heading = document.createElement('h3');
    const titleLink = document.createElement('a');
    titleLink.href = product.url;
    titleLink.textContent = product.title;
    heading.append(titleLink);

    const price = document.createElement('p');
    price.className = 'ow-zone-card__price';
    price.textContent = product.price;

    const note = document.createElement('p');
    note.className = 'ow-zone-card__note';
    note.textContent = result.note;

    const view = document.createElement('a');
    view.className = 'ow-zone-card__view';
    view.href = product.url;
    view.textContent = i18n.viewPlant;

    body.append(status, heading, price, note, view);
    card.append(media, body);
    return card;
  }

  class OwZoneFinder extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-zone-form]');
      this.zipInput = this.querySelector('[data-zone-zip]');
      this.error = this.querySelector('[data-zone-error]');
      this.loading = this.querySelector('[data-zone-loading]');
      this.results = this.querySelector('[data-zone-results]');
      this.grid = this.querySelector('[data-zone-grid]');
      this.empty = this.querySelector('[data-zone-empty]');
      this.moreWrap = this.querySelector('[data-zone-more-wrap]');
      this.moreButton = this.querySelector('[data-zone-more]');
      this.zoneValue = this.querySelector('[data-zone-value]');
      this.temperature = this.querySelector('[data-zone-temperature]');
      this.count = this.querySelector('[data-zone-count]');
      this.editButton = this.querySelector('[data-zone-edit]');
      this.refine = this.querySelector('[data-zone-refine]');
      this.ipHint = this.querySelector('[data-zone-ip-hint]');
      this.localMapUrl = this.dataset.zoneMapUrl || '';

      try {
        this.products = JSON.parse(this.querySelector('[data-zone-products]')?.textContent || '[]');
      } catch (_) {
        this.products = [];
      }

      const saved = readPreferences();
      if (saved?.zip) {
        this.zipInput.value = saved.zip;
        ['planting', 'sun', 'protect'].forEach((name) => {
          const input = this.form.querySelector(`[name="${name}"][value="${saved[name]}"]`);
          if (input) input.checked = true;
        });
        if (saved.zone) {
          this.form.hidden = true;
          this.render(saved);
        }
      } else {
        suggestZipFromIp(this.zipInput, this.ipHint);
      }

      bindZipSuggestion(this.zipInput, this.ipHint);

      this.form.addEventListener('submit', (event) => this.submit(event));
      this.moreButton.addEventListener('click', () => this.showAll());
      this.editButton.addEventListener('click', () => {
        this.form.hidden = false;
        this.results.hidden = true;
        this.error.textContent = '';
        this.zipInput.removeAttribute('aria-invalid');
        this.zipInput.focus();
        this.scrollIntoView({ behavior: 'smooth', block: 'center' });
      });
    }

    getPreferences() {
      const formData = new FormData(this.form);
      return {
        zip: this.zipInput.value.trim(),
        planting: formData.get('planting') || 'ground',
        sun: formData.get('sun') || 'full',
        protect: formData.get('protect') || 'yes'
      };
    }

    async submit(event) {
      event.preventDefault();
      const preferences = this.getPreferences();
      this.error.textContent = '';
      this.zipInput.removeAttribute('aria-invalid');

      if (!/^\d{5}$/.test(preferences.zip)) {
        this.error.textContent = i18n.invalidZip;
        this.zipInput.setAttribute('aria-invalid', 'true');
        this.zipInput.focus();
        return;
      }

      this.loading.hidden = false;
      this.results.hidden = true;
      this.form.querySelector('button[type="submit"]').disabled = true;

      try {
        const zoneData = await lookupZone(preferences.zip, this.localMapUrl);
        const stored = {
          ...preferences,
          zone: String(zoneData.zone).toLowerCase(),
          temperature: zoneData.trange || ''
        };
        savePreferences(stored);
        this.render(stored);
        document.dispatchEvent(new CustomEvent('ow:zone-updated', { detail: stored }));
      } catch (error) {
        this.error.textContent = error.message === 'zip_not_found'
          ? i18n.zipNotFound
          : i18n.lookupUnavailable;
        this.results.hidden = true;
      } finally {
        this.loading.hidden = true;
        this.form.querySelector('button[type="submit"]').disabled = false;
      }
    }

    render(preferences) {
      this.form.hidden = true;
      this.zoneValue.textContent = `${preferences.zip} · USDA Zone ${preferences.zone}`;
      this.temperature.textContent = preferences.temperature
        ? format(i18n.typicalWinterLow, { temperature: preferences.temperature.replace('to', '–') })
        : '';
      this.grid.replaceChildren();

      const recommendations = this.products
        .map((product) => {
          const profile = getProfile(product.title, product.handle, product);
          const result = recommendation(profile, preferences.zone, preferences);
          return result && result.status !== 'not' ? { product, result } : null;
        })
        .filter(Boolean)
        .sort((a, b) => a.result.rank - b.result.rank || a.product.title.localeCompare(b.product.title));

      this.recommendations = recommendations;
      this.expanded = false;
      this.count.textContent = format(i18n.currentMatches, { count: recommendations.length });
      if (this.refine) this.refine.open = false;
      this.renderCards();

      this.empty.hidden = recommendations.length > 0;
      this.results.hidden = false;
    }

    renderCards() {
      const initialLimit = this.closest('.ow-online') ? 4 : 8;
      const visible = this.expanded ? this.recommendations : this.recommendations.slice(0, initialLimit);
      this.grid.replaceChildren();
      visible.forEach(({ product, result }) => {
        this.grid.append(createProductCard(product, result));
      });

      const hasMore = this.recommendations.length > initialLimit;
      this.moreWrap.hidden = !hasMore;
      this.moreButton.setAttribute('aria-expanded', String(this.expanded));
      if (hasMore) this.moreButton.textContent = this.expanded
        ? this.moreButton.dataset.collapseLabel
        : format(i18n.viewAllMatches, { count: this.recommendations.length });
    }

    showAll() {
      this.expanded = !this.expanded;
      this.renderCards();
      if (!this.expanded) this.results.scrollIntoView({ block: 'start', behavior: 'auto' });
    }
  }

  class OwProductZone extends HTMLElement {
    connectedCallback() {
      if (this.initialized) return;
      this.initialized = true;
      this.form = this.querySelector('[data-product-zone-form]');
      this.zipInput = this.querySelector('[data-product-zone-zip]');
      this.error = this.querySelector('[data-product-zone-error]');
      this.answer = this.querySelector('[data-product-zone-answer]');
      this.zoneValue = this.querySelector('[data-product-zone-value]');
      this.output = this.querySelector('[data-product-zone-result]');
      this.typical = this.querySelector('[data-product-zone-typical]');
      this.status = this.querySelector('[data-product-zone-status]');
      this.changeButton = this.querySelector('[data-product-zone-change]');
      this.ipHint = this.querySelector('[data-zone-ip-hint]');
      this.localMapUrl = this.dataset.zoneMapUrl || '';
      this.profile = getProfile(this.dataset.productTitle, this.dataset.productHandle, {
        zoneMin: this.dataset.zoneMin,
        zoneMax: this.dataset.zoneMax
      });
      if (!this.profile || !this.output || !this.form) {
        this.hidden = true;
        return;
      }

      this.typical.textContent = format(i18n.typicalHardinessShort, { zones: this.profile.label });
      this.form.addEventListener('submit', (event) => this.submit(event));
      this.changeButton.addEventListener('click', () => this.showForm());

      const saved = readPreferences();
      if (saved?.zip) this.zipInput.value = saved.zip;
      else suggestZipFromIp(this.zipInput, this.ipHint);
      bindZipSuggestion(this.zipInput, this.ipHint);
      this.render(saved);
      document.addEventListener('ow:zone-updated', (event) => this.render(event.detail));
    }

    async submit(event) {
      event.preventDefault();
      const zip = this.zipInput.value.trim();
      const submitButton = this.form.querySelector('button[type="submit"]');
      this.error.textContent = '';

      if (!/^\d{5}$/.test(zip)) {
        this.error.textContent = i18n.invalidZip;
        this.zipInput.focus();
        return;
      }

      submitButton.disabled = true;
      submitButton.textContent = i18n.checking;

      try {
        this.form.setAttribute('aria-busy', 'true');
        const zoneData = await lookupZone(zip, this.localMapUrl);
        const previous = readPreferences() || {};
        const stored = {
          zip,
          planting: previous.planting || 'ground',
          sun: previous.sun || 'full',
          protect: previous.protect || 'yes',
          zone: String(zoneData.zone).toLowerCase(),
          temperature: zoneData.trange || ''
        };
        savePreferences(stored);
        this.render(stored);
        document.dispatchEvent(new CustomEvent('ow:zone-updated', { detail: stored }));
      } catch (error) {
        this.error.textContent = error.message === 'zip_not_found'
          ? i18n.zipNotFound
          : i18n.lookupUnavailable;
      } finally {
        this.form.removeAttribute('aria-busy');
        submitButton.disabled = false;
        submitButton.textContent = i18n.check;
      }
    }

    showForm() {
      this.form.hidden = false;
      this.answer.hidden = true;
      this.status.hidden = true;
      this.error.textContent = '';
      this.zipInput.focus();
    }

    render(preferences) {
      if (!preferences?.zone || !preferences?.zip) {
        this.form.hidden = false;
        this.answer.hidden = true;
        this.status.hidden = true;
        return;
      }

      const result = recommendation(this.profile, preferences.zone, preferences);
      const typical = format(i18n.typicalHardiness, { zones: this.profile.label });
      const detail = result.note.startsWith(typical) ? result.note.slice(typical.length).trim() : result.note;
      const advice = detail || (result.status === 'great'
        ? i18n.strongOutdoorMatch
        : result.productLabel);
      this.zipInput.value = preferences.zip;
      this.zoneValue.textContent = `${preferences.zip} · USDA Zone ${preferences.zone}`;
      this.output.textContent = advice;
      this.status.className = `ow-zone-status ow-zone-status--${result.status}`;
      this.status.textContent = result.productLabel;
      this.status.hidden = false;
      this.form.hidden = true;
      this.answer.hidden = false;
      this.error.textContent = '';
    }
  }

  if (!customElements.get('ow-zone-finder')) customElements.define('ow-zone-finder', OwZoneFinder);
  if (!customElements.get('ow-product-zone')) customElements.define('ow-product-zone', OwProductZone);
})();
